import { PrismColor } from './prismSystem';

export type ChoiceRewardType = 'coin_stack' | 'energy_pack' | 'starter_bolt' | 'prism' | 'black_prism' | 'star_ticket';

export interface Reward {
  type: ChoiceRewardType;
  rarity: 'common' | 'rare' | 'epic' | 'legendary' | 'jackpot';
  amount: number;
  label: string;
  stars?: number;
  color?: PrismColor;
}

export const BRONZE_REWARDS: { chance: number; type: ChoiceRewardType; rarity: Reward['rarity']; stars?: number; amount?: number }[] = [
  { chance: 60.0, type: 'prism', rarity: 'common', stars: 1 },
  { chance: 20.0, type: 'prism', rarity: 'rare', stars: 2 },
  { chance: 4.0, type: 'coin_stack', rarity: 'rare', amount: 5000 },
  { chance: 10.0, type: 'coin_stack', rarity: 'common', amount: 2500 },
  { chance: 1.0, type: 'starter_bolt', rarity: 'epic', amount: 10 },
  { chance: 4.9, type: 'energy_pack', rarity: 'common', amount: 1 },
  { chance: 0.1, type: 'star_ticket', rarity: 'epic', amount: 1 },
];

export const SILVER_REWARDS: { chance: number; type: ChoiceRewardType; rarity: Reward['rarity']; stars?: number; amount?: number }[] = [
  { chance: 35.0, type: 'starter_bolt', rarity: 'common', amount: 10 },
  { chance: 15.0, type: 'starter_bolt', rarity: 'epic', amount: 25 },
  { chance: 5.0, type: 'starter_bolt', rarity: 'epic', amount: 50 },
  { chance: 25.0, type: 'prism', rarity: 'common', stars: 1 },
  { chance: 12.5, type: 'prism', rarity: 'rare', stars: 2 },
  { chance: 0.5, type: 'prism', rarity: 'epic', stars: 3 },
  { chance: 5.0, type: 'energy_pack', rarity: 'common', amount: 1 },
  { chance: 1.5, type: 'energy_pack', rarity: 'rare', amount: 5 },
  { chance: 0.5, type: 'star_ticket', rarity: 'epic', amount: 1 },
];

export const GOLD_REWARDS: { chance: number; type: ChoiceRewardType; rarity: Reward['rarity']; stars?: number; amount?: number }[] = [
  { chance: 25.0, type: 'coin_stack', rarity: 'common', amount: 10000 },
  { chance: 25.0, type: 'prism', rarity: 'rare', stars: 2 },
  { chance: 15.0, type: 'coin_stack', rarity: 'rare', amount: 30000 },
  { chance: 6.0, type: 'prism', rarity: 'epic', stars: 3 },
  { chance: 10.0, type: 'energy_pack', rarity: 'rare', amount: 5 },
  { chance: 5.0, type: 'energy_pack', rarity: 'rare', amount: 10 },
  { chance: 5.0, type: 'coin_stack', rarity: 'rare', amount: 50000 },
  { chance: 4.0, type: 'coin_stack', rarity: 'epic', amount: 100000 },
  { chance: 4.0, type: 'star_ticket', rarity: 'epic', amount: 1 },
  { chance: 1.0, type: 'coin_stack', rarity: 'legendary', amount: 250000 },
];

export const DIAMOND_REWARDS: { chance: number; type: ChoiceRewardType; rarity: Reward['rarity']; stars?: number; amount?: number }[] = [
  { chance: 50.0, type: 'prism', rarity: 'rare', stars: 2 },
  { chance: 34.5, type: 'prism', rarity: 'epic', stars: 3 },
  { chance: 10.0, type: 'energy_pack', rarity: 'rare', amount: 10 },
  { chance: 4.0, type: 'star_ticket', rarity: 'epic', amount: 1 },
  { chance: 1.0, type: 'starter_bolt', rarity: 'epic', amount: 100 },
  { chance: 0.4, type: 'star_ticket', rarity: 'legendary', amount: 5 },
  { chance: 0.1, type: 'starter_bolt', rarity: 'legendary', amount: 500 }
];

export function openBox(tier: string): Reward {
  const roll = Math.random() * 100;
  let cumulative = 0;
  const config = tier === 'diamond' ? DIAMOND_REWARDS : tier === 'gold' ? GOLD_REWARDS : tier === 'silver' ? SILVER_REWARDS : BRONZE_REWARDS;

  for (const rewardConfig of config) {
    cumulative += rewardConfig.chance;
    if (roll <= cumulative) {
      if (rewardConfig.type === 'black_prism') {
        return { type: 'black_prism', rarity: 'jackpot', amount: 1, label: 'Black Anima' };
      }

      if (rewardConfig.type === 'prism') {
        const stars = rewardConfig.stars || 1;
        const colors: PrismColor[] = ['red', 'yellow', 'green', 'blue', 'violet'];
        const color = colors[Math.floor(Math.random() * colors.length)];
        const colorLabel = color.charAt(0).toUpperCase() + color.slice(1);
        return { 
          type: 'prism', 
          rarity: rewardConfig.rarity, 
          amount: 1, 
          stars, 
          color,
          label: `${colorLabel} ${stars}-Star Anima` 
        };
      }

      if (rewardConfig.type === 'star_ticket') {
        const amt = rewardConfig.amount || 1;
        return { type: 'star_ticket', rarity: rewardConfig.rarity, amount: amt, label: amt > 1 ? `${amt} Star Tickets` : 'Star Ticket' };
      }

      if (rewardConfig.type === 'coin_stack') {
        const amt = rewardConfig.amount || 100;
        return { type: 'coin_stack', rarity: rewardConfig.rarity, amount: amt, label: `${amt.toLocaleString()} Coins` };
      }

      if (rewardConfig.type === 'starter_bolt') {
        const amt = rewardConfig.amount || 10;
        return { type: 'starter_bolt', rarity: rewardConfig.rarity, amount: amt, label: `${amt.toLocaleString()} Hoshi` };
      }

      if (rewardConfig.type === 'energy_pack') {
        const amt = rewardConfig.amount || 1;
        return { type: 'energy_pack', rarity: rewardConfig.rarity, amount: amt, label: `${amt} Heart Ticket${amt > 1 ? 's' : ''}` };
      }
    }
  }

  // Fallback if roll exceeds total chance (e.g. if chances sum to < 100)
  return { type: 'starter_bolt', rarity: 'common', amount: 100, label: '100 Hoshi' };
}
